<template>
  <div
    class="main-input flex flex-col gap-1"
    :class="[{ error: hasError, 'text-state-fgr-disabled': readonly }, props.class]"
    @click="() => emit('click')"
  >
    <label v-if="label && !labelInline" class="flex items-center gap-1 text-left" :for="id">
      {{ label }}
      <slot name="label_suffix" /> <span v-if="isRequired" class="text-fgr-critical">*</span>
    </label>
    <div
      ref="wrapper"
      class="old-input-wrapper bg-input-bg-default hover:bg-input-bg-hover focus-within:bg-input-bg-active! text-input-fgr-filled hover:text-input-fgr-hover focus-within:text-input-fgr-active! focus-within:border-state-focus-border focus-within:outline-input-bdr-active flex items-center rounded-lg px-2.5 py-0.75 -outline-offset-1 transition-all duration-300 focus-within:outline"
      :class="[
        {
          'border-input-bdr-default focus-within:border-input-bdr-active hover:border-input-bdr-hover border':
            !borderless,
          'bg-input-bg-critical! text-input-fgr-critical! border-input-fgr-critical! border': hasError,
          'bg-input-bg-disabled! hover:bg-input-bg-disabled! border-input-bg-disabled! hover:border-input-bg-disabled! text-input-fgr-disabled! hover:text-input-fgr-disabled!':
            readonly,
        },
      ]"
    >
      <label
        v-if="label && labelInline"
        :for="id"
        class="text-fgr-secondary mr-2 flex items-center gap-2 text-left text-nowrap"
      >
        {{ label }}
        <slot name="label_suffix" />
      </label>

      <div v-else-if="prefix || slots.prefix" ref="prefixWrapper" class="mr-2 flex items-center text-sm font-normal">
        <slot name="prefix">
          {{ prefix }}
        </slot>
      </div>
      <slot>
        <input
          v-if="tag == 'input'"
          v-bind="$attrs"
          :id
          ref="input"
          v-model="modelValue"
          class="placeholder:input-fgr-read-only cursor-inherit h-5 w-full truncate focus:ring-0 focus:outline-none"
          :type="type"
          :name="name"
          :class="{
            'mr-2': suffix || slots.suffix,
            'mr-1': !suffix && !slots.suffix,
            'text-right': textRight,
          }"
          :readonly="readonly"
          :disabled="readonly"
          :min="type === 'number' ? min : undefined"
          :max="type === 'number' ? max : undefined"
          :minlength="type === 'text' ? min : undefined"
          :maxlength="type === 'text' ? max : undefined"
          :aria-label="ariaLabel"
          :data-testid="name || testId"
          @blur="onBlur"
          @focus="hasFocus = true"
        />
      </slot>
      <div
        v-if="suffix || slots.suffix || clearable"
        ref="suffixWrapper"
        class="flex h-5 w-full max-w-max items-center text-sm font-normal"
      >
        <slot name="suffix">
          {{ suffix }}
        </slot>

        <Button
          v-if="modelValue && clearable"
          icon-only
          :variant="ButtonVariant.Tertiary"
          @click="() => (modelValue = '')"
        >
          <Icon src="XCircle" />
        </Button>
      </div>
      <slot name="button" />
      <textarea
        v-if="tag == 'textarea'"
        :id
        v-bind="$attrs"
        ref="input"
        v-model="modelValue"
        class="h-24 min-h-24 w-full"
        :readonly="readonly"
        :disabled="readonly"
        :minlength="min"
        :maxlength="max"
        @blur="() => emit('blur')"
      />
    </div>

    <Message v-if="(min || max) && type !== 'number'" class="text-body-small" :variant="ButtonVariant.Default">
      <span v-if="min">{{ $t("minimum_n_characters", { n: min }) }}</span>
      <span v-if="max" class="ms-auto">{{ typeof modelValue === "string" ? modelValue.length : 0 }} / {{ max }}</span>
    </Message>
    <Message v-if="hasError" variant="invalid">{{ error }}</Message>
    <Message v-if="message" :variant="ButtonVariant.Default">{{ message }}</Message>
  </div>
</template>

<script setup lang="ts">
  const slots = useSlots();
  defineOptions({
    name: "InputWrapper",
  });

  const [modelValue, modifiers] = defineModel<string | number>({
    set(value) {
      const m = getModifiers(modifiers, value);
      if (m.subdomain) {
        return companyNameSanitizer(value);
      }
      if (typeof value === "string") {
        if (m.trim) {
          return value.trim();
        }
        if (m.noWhitespace) {
          return value.replace(/\s/g, "");
        }
        if (m.numeric) {
          return value.replace(/[^0-9.]/g, "");
        }
        if (m.multi) {
          //split on newline and if only one line, return string in array
          let v = [];
          if (props.type === "text") {
            v = value.split(",").length > 1 ? value.split(",") : [value];
          } else {
            v = value.split("\n").length > 1 ? value.split("\n") : [value];
          }
          //remove empty lines except for the last one
          return v
            .map((line, idx) => {
              if (idx < v.length - 1) {
                return line.trim();
              }
              return line;
            })
            .filter((line, idx) => line || idx === v.length - 1);
        }
      }

      return value;
    },
    get(value) {
      if (!value) return value;
      const m = getModifiers(modifiers, value);

      if (m.multi && Array.isArray(value)) {
        if (props.type === "text") {
          return value.join(",");
        }
        return value.join("\n");
      }
      return value;
    },
  });

  const id = `input-${crypto.randomUUID()}`;
  const input = ref<HTMLElement | null>(null);
  const wrapper = ref<HTMLElement | null>(null);
  const prefixWrapper = ref<HTMLElement | null>(null);
  const suffixWrapper = ref<HTMLElement | null>(null);
  const hasFocus = ref(false);

  const props = withDefaults(
    defineProps<{
      ariaLabel?: string;
      autofocus?: boolean;
      borderless?: boolean;
      class?: string | null | object | Array<string | object>;
      clearable?: boolean;
      error?: string | null;
      label?: string | null;
      labelInline?: boolean;
      max?: number;
      message?: string;
      min?: number;
      modifiers?: string[];
      name?: string;
      prefix?: string | null;
      readonly?: boolean;
      suffix?: string | null;
      tag?: "input" | "textarea";
      testId?: string;
      textRight?: boolean;
      isRequired?: boolean;
      type?:
        | "button"
        | "checkbox"
        | "color"
        | "date"
        | "datetime-local"
        | "email"
        | "file"
        | "hidden"
        | "image"
        | "month"
        | "number"
        | "password"
        | "radio"
        | "range"
        | "reset"
        | "search"
        | "submit"
        | "tel"
        | "text"
        | "time"
        | "url"
        | "week";
    }>(),
    {
      ariaLabel: undefined,
      autofocus: false,
      borderless: false,
      class: undefined,
      clearable: false,
      error: undefined,
      label: undefined,
      labelInline: false,
      max: undefined,
      message: undefined,
      min: undefined,
      modifiers: () => [],
      name: "",
      prefix: undefined,
      readonly: false,
      suffix: undefined,
      tag: "input",
      testId: undefined,
      textRight: false,
      isRequired: false,
      type: "text",
    }
  );

  const emit = defineEmits(["click", "blur"]);

  const getModifiers = (modifiers, value): { [key: string]: any } => {
    //transform props.modifiers to object with true values for each modifier
    let m = props.modifiers
      ? props.modifiers.reduce((acc, mdf) => {
          acc[mdf] = true;
          return acc;
        }, {})
      : {};

    //merge with modelmodifiers
    m = Object.assign(m, modifiers.value, { value });

    return m;
  };

  onMounted(() => {
    if (props.autofocus) {
      setTimeout(() => {
        input.value?.focus();
      }, 0);
    }

    //If prefix or suffix is present, and they are not buttons or anchors, focus input on click
    if (
      prefixWrapper.value &&
      !prefixWrapper.value.querySelector("button") &&
      !prefixWrapper.value.querySelector("a")
    ) {
      prefixWrapper.value.addEventListener("click", () => {
        input.value?.focus();
      });
    }
    if (
      suffixWrapper.value &&
      !suffixWrapper.value.querySelector("button") &&
      !suffixWrapper.value.querySelector("a")
    ) {
      suffixWrapper.value.addEventListener("click", () => {
        input.value?.focus();
      });
    }
  });

  const onBlur = (evt: FocusEvent) => {
    emit("blur", evt);
    hasFocus.value = false;
  };

  const hasError = computed(() => {
    return (
      !!props.error &&
      !hasFocus.value &&
      !props.readonly &&
      (modelValue.value !== "" || (modelValue.value === "" && props.error))
    );
  });

  defineExpose({
    input,
    wrapper,
  });
</script>
